import React, { useEffect, useState, useRef, useContext, createContext } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { fmtGbp, fmtGbp0, fmtNum, fmtNum0, noteTableHeaders } from "@/lib/constants";
import { Printer, Minus, Plus, ArrowUp, ArrowDown, X } from "lucide-react";
import { cleanHtml, cleanReportHtml, PAGE_BREAK_TOKEN } from "@/lib/sanitize";
import { buildSections } from "@/lib/accountSections";
import { toast } from "sonner";

class _DbgBoundary extends React.Component {
    constructor(p) { super(p); this.state = { err: null }; }
    static getDerivedStateFromError(err) { return { err }; }
    componentDidCatch(err, info) { console.error("Accounts document render failed:", err, info); }
    render() {
        if (this.state.err) return (
            <div className="no-print" data-testid="print-error" style={{ padding: 40, fontFamily: "'IBM Plex Sans',sans-serif", color: "#334155", maxWidth: 640, margin: "40px auto" }}>
                <h2 style={{ color: "#b45309", fontFamily: "'Chivo',sans-serif" }}>This document couldn't be generated</h2>
                <p>Something went wrong while building the accounts for printing. Your data is safe — nothing has been changed. Please try again, and if it keeps happening let the Qube Hub team know.</p>
            </div>
        );
        return this.props.children;
    }
}

export default function AccountsPrint() {
    const { engagementId } = useParams();
    const [sp] = useSearchParams();
    const doc = sp.get("doc") || "pack";
    const [e, setE] = useState(null);
    const [acc, setAcc] = useState(null);
    const [reports, setReports] = useState(null);
    // Headless PDF render seeds a short-lived token via ?t= so the print page can authenticate.
    useState(() => { const t = sp.get("t"); if (t) { try { localStorage.setItem("qube_token", t); } catch (_) { /* ignore */ } } return null; });

    useEffect(() => {
        Promise.all([
            api.get(`/engagements/${engagementId}`),
            api.get(`/engagements/${engagementId}/accounts`),
            api.get(`/engagements/${engagementId}/reports`),
        ]).then(([a, b, c]) => { setE(a.data); setAcc(b.data); setReports(c.data.reports); }).catch(() => {});
    }, [engagementId]);

    useEffect(() => {
        if (e && acc && reports && !sp.get("noprint") && !sp.get("pdf")) {
            let cancelled = false;
            const go = () => { if (!cancelled) window.print(); };
            const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
            ready.then(() => setTimeout(go, 500));
            return () => { cancelled = true; };
        }
    }, [e, acc, reports]);
    if (!e || !acc || !reports) return <div style={{ padding: 40 }}>Preparing document…</div>;

    const preview = !!sp.get("noprint");
    const reloadReports = () => api.get(`/engagements/${engagementId}/reports`).then((c) => setReports(c.data.reports)).catch(() => {});
    if (doc === "repletter") {
        const byKey = Object.fromEntries(reports.map((r) => [r.key, r]));
        return <PrintShell watermark={false} preview={preview}><Doc html={byKey.rep_letter?.rendered} testid="print-repletter" /></PrintShell>;
    }
    return <_DbgBoundary><AccountsPack e={e} acc={acc} reports={reports} watermark={!(acc.is_approved && acc.watermark_removed)} interactive={false} preview={preview} reloadReports={reloadReports} /></_DbgBoundary>;
}

export const PackCtx = createContext({ interactive: false });

export function AccountsPack({ e, acc, reports, watermark, interactive, preview, reloadReports }) {
    const money = acc.round_whole ? fmtNum0 : fmtNum;
    const isAudit = (e.engagement_type || "").includes("audit");
    const byKey = Object.fromEntries(reports.map((r) => [r.key, r]));
    const primary = isAudit ? byKey.audit_report : byKey.roff;
    const primaryKey = isAudit ? "audit_report" : "roff";
    const noteByNum = Object.fromEntries((acc.notes?.list || []).map((n) => [n.number, n]));
    const shareUrl = acc.share_token ? `${window.location.origin}/shared/accounts/${acc.share_token}` : null;

    // One-click "insert page break here" from a preview break-hint on an editable report section.
    // We map the on-screen boundary to an ordinal position among the report's non-empty, non-break
    // blocks (robust to the sanitizer stripping empty <p> and converting breaks to divs), then insert
    // a page-break paragraph into the report's raw HTML (tokens preserved) and save it.
    const insertBreakAt = async (btn, reportKey) => {
        try {
            const marker = btn.closest(".break-hint");
            const section = btn.closest(".page");
            const contentEl = section && section.querySelector(".rte-content");
            if (!contentEl || !marker) return;
            const y = marker.getBoundingClientRect().top;
            let ordinal = 0, target = -1;
            for (const b of Array.from(contentEl.children)) {
                if (b.classList.contains("manual-break")) continue;
                if (target < 0 && b.getBoundingClientRect().bottom > y + 2) target = ordinal;
                ordinal++;
            }
            const raw = (byKey[reportKey] && byKey[reportKey].raw) || "";
            const root = new DOMParser().parseFromString(`<body>${raw}</body>`, "text/html").body;
            const isBreak = (n) => n.tagName === "P" && /(\u27E8PAGE BREAK\u27E9|\[\[PAGEBREAK\]\])/i.test(n.textContent || "");
            const isEmpty = (n) => n.tagName === "P" && !(n.textContent || "").trim() && !n.querySelector("img,table");
            let k = 0, before = null;
            for (const n of Array.from(root.children)) {
                if (isBreak(n) || isEmpty(n)) continue;
                if (k === target) { before = n; break; }
                k++;
            }
            const pb = root.ownerDocument.createElement("p");
            pb.textContent = PAGE_BREAK_TOKEN;
            if (before) root.insertBefore(pb, before); else root.appendChild(pb);
            await api.put(`/engagements/${e.id}/reports/${reportKey}`, { content: root.innerHTML });
            toast.success("Page break added — the report will re-flow");
            if (reloadReports) reloadReports();
        } catch (err) {
            toast.error("Couldn't add the page break here");
        }
    };

    // One-click "remove this break" for an existing manual page break shown in the preview. The Nth
    // .manual-break div in a report section maps 1:1 to the Nth page-break paragraph in its raw HTML.
    const removeBreakAt = async (reportKey, ordinal) => {
        try {
            const raw = (byKey[reportKey] && byKey[reportKey].raw) || "";
            const root = new DOMParser().parseFromString(`<body>${raw}</body>`, "text/html").body;
            const isBreak = (n) => n.tagName === "P" && /(\u27E8PAGE BREAK\u27E9|\[\[PAGEBREAK\]\])/i.test(n.textContent || "");
            const found = Array.from(root.children).filter(isBreak);
            const toRemove = found[ordinal];
            if (!toRemove) { toast.error("Couldn't find that break"); return; }
            toRemove.remove();
            await api.put(`/engagements/${e.id}/reports/${reportKey}`, { content: root.innerHTML });
            toast.success("Page break removed — the report will re-flow");
            if (reloadReports) reloadReports();
        } catch (err) {
            toast.error("Couldn't remove that page break");
        }
    };

    // Nudge an existing manual page break one paragraph earlier (dir<0) or later (dir>0) in the report
    // raw, so staff can fine-tune where a long report splits without opening the editor.
    const moveBreakAt = async (reportKey, ordinal, dir) => {
        try {
            const raw = (byKey[reportKey] && byKey[reportKey].raw) || "";
            const root = new DOMParser().parseFromString(`<body>${raw}</body>`, "text/html").body;
            const isBreak = (n) => n.tagName === "P" && /(\u27E8PAGE BREAK\u27E9|\[\[PAGEBREAK\]\])/i.test(n.textContent || "");
            const isEmpty = (n) => n.tagName === "P" && !(n.textContent || "").trim() && !n.querySelector("img,table");
            const node = Array.from(root.children).filter(isBreak)[ordinal];
            if (!node) { toast.error("Couldn't find that break"); return; }
            if (dir < 0) {
                let prev = node.previousElementSibling;
                while (prev && (isBreak(prev) || isEmpty(prev))) prev = prev.previousElementSibling;
                if (!prev) { toast.info("The break is already as high as it can go"); return; }
                root.insertBefore(node, prev);
            } else {
                let next = node.nextElementSibling;
                while (next && (isBreak(next) || isEmpty(next))) next = next.nextElementSibling;
                if (!next) { toast.info("The break is already as low as it can go"); return; }
                if (next.nextElementSibling) root.insertBefore(node, next.nextElementSibling); else root.appendChild(node);
            }
            await api.put(`/engagements/${e.id}/reports/${reportKey}`, { content: root.innerHTML });
            toast.success("Page break moved — the report will re-flow");
            if (reloadReports) reloadReports();
        } catch (err) {
            toast.error("Couldn't move that page break");
        }
    };

    // Build content keyed by stable section keys, then order + filter per the saved layout.
    const map = {};
    if (byKey.ma_report) map.ma = { id: "sec-ma", testid: "print-ma", label: "Managing Agent's Report", html: byKey.ma_report.rendered, reportKey: "ma_report" };
    if (primary) map.primary = { id: "sec-primary", testid: "print-primary", label: isAudit ? "Independent Auditor's Report" : "Report of Factual Findings", html: primary.rendered, reportKey: primaryKey };
    map.is = { id: "sec-is", testid: "print-is", label: "Service Charge Income & Expenditure Account", node: <ISBlock block={acc.income_statement.consolidated} hasNote={acc.income_statement.has_note_col} title="Service Charge Income & Expenditure Account" showBudget={acc.show_budget} showPrior={acc.show_prior} money={money} period={acc.period} headerHtml={acc.income_statement.header_html} labels={acc.col_labels} /> };
    acc.income_statement.per_schedule.forEach((b) => { map[`is-${slug(b.schedule)}`] = { id: `sec-is-${slug(b.schedule)}`, testid: `print-is-${slug(b.schedule)}`, label: `Income & Expenditure — ${b.schedule}`, node: <ISBlock block={b} hasNote={acc.income_statement.has_note_col} title={`Income & Expenditure — ${b.schedule}`} showBudget={acc.show_budget} showPrior={acc.show_prior} money={money} period={acc.period} labels={acc.col_labels} entity={e.client_name} /> }; });
    map.bs = { id: "sec-bs", testid: "print-bs", label: "Service Charge Balance Sheet", node: <BSBlock bs={acc.balance_sheet} hasNote={acc.balance_sheet.has_note_col} showPrior={acc.show_prior} money={money} agent={e.managing_agent} entity={e.client_name} headerHtml={acc.balance_sheet.header_html} labels={acc.col_labels} /> };
    map.notes = { id: "sec-notes", testid: "print-notes", label: "Notes to the Service Charge Accounts", flow: true, node: <NotesBlock notes={acc.notes} showPrior={acc.show_prior} money={money} entity={e.client_name} period={acc.period} labels={acc.col_labels} periodEnds={acc.period_ends || {}} /> };
    const content = buildSections(acc, acc.section_order, acc.hidden_sections)
        .filter((s) => !s.hidden && map[s.key])
        .map((s) => map[s.key]);

    // Page numbers for browser print-to-PDF: the browser doesn't expose pagination to the page,
    // so we measure each section at the real print width (178mm) and A4 printable height (273mm)
    // to work out which page each section starts/ends on. Cover = 1, Contents = 2.
    const [pages, setPages] = useState({ starts: {}, ends: {}, total: 0 });
    const [breaks, setBreaks] = useState({});
    const [manualBreaks, setManualBreaks] = useState({});
    useEffect(() => {
        let cancelled = false;
        const run = () => {
            if (cancelled) return;
            const root = document.querySelector(".print-root");
            if (!root) return;
            const ruler = document.createElement("div");
            ruler.style.cssText = "position:absolute;visibility:hidden;height:100mm;width:10mm";
            root.appendChild(ruler);
            const pxPerMm = ruler.getBoundingClientRect().height / 100 || 3.78;
            root.removeChild(ruler);
            const sheetPx = 273 * pxPerMm;
            const widthPx = 178 * pxPerMm;
            const holder = document.createElement("div");
            holder.className = "print-root";
            holder.style.cssText = `position:absolute;left:-99999px;top:0;width:${widthPx}px`;
            document.body.appendChild(holder);
            let page = 3;
            const starts = {}, ends = {};
            content.forEach((c) => {
                starts[c.id] = page;
                const el = document.getElementById(c.id);
                let h = sheetPx;
                if (el) {
                    const clone = el.cloneNode(true);
                    clone.style.cssText = `max-width:none;width:${widthPx}px;padding:6mm 4mm;margin:0`;
                    holder.appendChild(clone);
                    h = clone.getBoundingClientRect().height;
                    holder.removeChild(clone);
                }
                const used = Math.max(1, Math.ceil((h - 2) / sheetPx));
                ends[c.id] = page + used - 1;
                page += used;
            });
            document.body.removeChild(holder);
            if (!cancelled) setPages({ starts, ends, total: page - 1 });
        };
        const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
        ready.then(() => setTimeout(run, 150));
        return () => { cancelled = true; };
    }, [acc, reports]); // eslint-disable-line react-hooks/exhaustive-deps

    // On-screen preview only: find where multi-page sections cross an A4 sheet edge so we can
    // show a subtle "suggested page break" marker. Measured in mm, so it stays aligned under zoom.
    useEffect(() => {
        if (!preview) return;
        let cancelled = false;
        const run = () => {
            if (cancelled) return;
            const zone = document.querySelector(".print-zoom") || document.querySelector(".preview-sheets");
            if (!zone) return;
            const ruler = document.createElement("div");
            ruler.style.cssText = "position:absolute;visibility:hidden;height:100mm;width:10mm";
            zone.appendChild(ruler);
            const pxPerMm = ruler.getBoundingClientRect().height / 100 || 3.78;
            zone.removeChild(ruler);
            const b = {};
            const mbs = {};
            content.forEach((c) => {
                const el = document.getElementById(c.id);
                if (!el) return;
                const secTop = el.getBoundingClientRect().top;
                const hMm = el.getBoundingClientRect().height / pxPerMm;
                const marks = [];
                for (let k = 0; 296 + k * 297 < hMm - 8; k++) marks.push(296 + k * 297);
                if (marks.length) b[c.id] = marks;
                if (c.reportKey) {
                    const found = Array.from(el.querySelectorAll(".manual-break")).map((mb, ordinal) => ({
                        ordinal,
                        top: (mb.getBoundingClientRect().top - secTop) / pxPerMm,
                    }));
                    if (found.length) mbs[c.id] = found;
                }
            });
            if (!cancelled) { setBreaks(b); setManualBreaks(mbs); }
        };
        const ready = document.fonts && document.fonts.ready ? document.fonts.ready : Promise.resolve();
        ready.then(() => setTimeout(run, 250));
        return () => { cancelled = true; };
    }, [acc, reports, preview]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <PackCtx.Provider value={{ interactive, noteByNum, money, showPrior: acc.show_prior, labels: acc.col_labels, periodEnds: acc.period_ends || {} }}>
        <PrintShell watermark={watermark} preview={preview}>
            {/* Cover */}
            <section className="page cover brk" data-testid="print-cover">
                <div className="cover-inner">
                    <div className="brand">QUBE ACCOUNTANTS</div>
                    <h1>{e.client_name}</h1>
                    {e.property_name ? <div className="prop">{e.property_name}</div> : null}
                    <div className="doctitle">Service Charge Accounts</div>
                    <div className="period">For the {acc.period || "period"}</div>
                    {e.managing_agent && !/^self[\s-]*managed$/i.test((e.managing_agent || "").trim()) ? <div className="agent">Managing agent: {e.managing_agent}</div> : null}
                    <div className="prep">Prepared by Qube Accountants Limited</div>
                </div>
            </section>
            {/* Contents */}
            <section className="page brk" data-testid="print-contents">
                <h2>Contents</h2>
                <ol className="contents">
                    {content.map((c) => (
                        <li key={c.id}><a href={`#${c.id}`}>{c.label}</a><span className="dots" /><span className="pg">{pages.starts[c.id] || ""}</span></li>
                    ))}
                </ol>
                {shareUrl && <p className="share-foot" data-testid="print-share-foot">To view an interactive version of these accounts please follow this <a href={shareUrl} target="_blank" rel="noreferrer">link</a>.</p>}
                <div className="pagenum">Page 2</div>
            </section>
            {content.map((c) => (
                <section className={`page ${c.flow ? "" : "brk"}`} key={c.id} id={c.id} data-testid={c.testid}>
                    {c.html !== undefined
                        ? <div className="rte-content" dangerouslySetInnerHTML={{ __html: cleanReportHtml(c.html || "<p><em>Template not published yet.</em></p>") }} />
                        : c.node}
                    {preview && (breaks[c.id] || []).map((topMm, i) => (
                        <div key={`bk-${i}`} className="break-hint no-print" style={{ top: `calc(${topMm}mm - 18mm)` }} data-testid="preview-break-hint">
                            {c.reportKey
                                ? <button type="button" className="break-hint-btn" data-testid="preview-break-insert" onClick={(ev) => insertBreakAt(ev.currentTarget, c.reportKey)}>+ Insert page break here</button>
                                : <span className="break-hint-label">Suggested page break</span>}
                        </div>
                    ))}
                    {preview && c.reportKey && (manualBreaks[c.id] || []).map((mb) => (
                        <div key={`mb-${mb.ordinal}`} className="break-existing no-print" style={{ top: `calc(${mb.top}mm - 18mm)` }}>
                            <div className="break-existing-tools">
                                <button type="button" className="break-move" data-testid="preview-break-up" title="Move this break up one paragraph" onClick={() => moveBreakAt(c.reportKey, mb.ordinal, -1)}><ArrowUp size={12} /></button>
                                <button type="button" className="break-move" data-testid="preview-break-down" title="Move this break down one paragraph" onClick={() => moveBreakAt(c.reportKey, mb.ordinal, 1)}><ArrowDown size={12} /></button>
                                <button type="button" className="break-remove" data-testid="preview-break-remove" title="Remove this break" onClick={() => removeBreakAt(c.reportKey, mb.ordinal)}><X size={12} /> Remove</button>
                            </div>
                        </div>
                    ))}
                    <div className="pagenum">{pages.ends[c.id] ? `Page ${pages.ends[c.id]}` : ""}</div>
                </section>
            ))}
        </PrintShell>
        </PackCtx.Provider>
    );
}

function slug(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "") || "x"; }

const Doc = ({ html, testid, anchorId }) => <section className="page" id={anchorId} data-testid={testid}><div className="rte-content" dangerouslySetInnerHTML={{ __html: cleanReportHtml(html || "<p><em>Template not published yet.</em></p>") }} /></section>;

function NoteRef({ n }) {
    const { interactive, noteByNum, money, showPrior, labels, periodEnds } = useContext(PackCtx);
    const [pos, setPos] = useState(null);
    const ref = useRef(null);
    const timer = useRef(null);
    if (!n) return "";
    const note = noteByNum?.[n];
    const open = () => { if (!interactive || !note) return; clearTimeout(timer.current); const r = ref.current?.getBoundingClientRect(); if (r) setPos({ top: r.bottom + 2, left: Math.max(8, Math.min(r.left - 20, window.innerWidth - 380)) }); };
    const scheduleHide = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setPos(null), 300); };
    return (
        <span ref={ref} onMouseEnter={open} onMouseLeave={scheduleHide} style={{ position: "relative" }}>
            <a href={`#note-${n}`} className="noteref" data-testid={interactive ? `share-noteref-${n}` : undefined}>{n}</a>
            {pos && note && <NoteHoverCard note={note} money={money} showPrior={showPrior} labels={labels} periodEnds={periodEnds} pos={pos} onEnter={() => clearTimeout(timer.current)} onLeave={scheduleHide} />}
        </span>
    );
}

function NoteHoverCard({ note, money, showPrior, labels, periodEnds, pos, onEnter, onLeave }) {
    const L = labels || { current: "Current Period", prior: "Prior Period" };
    return (
        <div onMouseEnter={onEnter} onMouseLeave={onLeave}
            style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 200, width: 360, maxHeight: 340, overflow: "auto", textAlign: "left" }}
            className="note-hovercard" data-testid="share-note-hovercard">
            <div className="hc-title">{note.number}. {note.title}</div>
            {(note.rendered_body || note.body_html) && <div className="rte-content" dangerouslySetInnerHTML={{ __html: cleanReportHtml(note.rendered_body || note.body_html) }} />}
            {(note.tables || []).map((tb, ti) => {
                if ((tb.rows || []).length === 0) return null;
                const hdr = noteTableHeaders(tb, L, periodEnds);
                return (
                <div key={ti}>
                    {tb.caption ? <p className="note-tbl-caption">{tb.caption}</p> : null}
                    <table><thead><tr><th>{hdr.desc}</th><th className="n">{hdr.current} (£)</th>{showPrior && tb.show_prior !== false && <th className="n">{hdr.prior} (£)</th>}</tr></thead>
                        <tbody>{tb.rows.map((r, i) => <tr key={i}><td>{r.label}</td><td className="n">{money(r.current)}</td>{showPrior && tb.show_prior !== false && <td className="n">{money(r.prior)}</td>}</tr>)}
                            {tb.show_total !== false && <tr className="tot strong"><td>{tb.total_label || "Total"}</td><td className="n">{money(tb.rows.reduce((s, r) => s + r.current, 0))}</td>{showPrior && tb.show_prior !== false && <td className="n">{money(tb.rows.reduce((s, r) => s + r.prior, 0))}</td>}</tr>}</tbody></table>
                </div>
                );
            })}
        </div>
    );
}

function ISBlock({ block, title, showBudget, showPrior, money, hasNote, period, headerHtml, labels, entity }) {
    const L = labels || { current: "Current Period", prior: "Prior Period", budget: "Budget" };
    hasNote = !!hasNote;
    const span = 2 + (showBudget ? 1 : 0) + (showPrior ? 1 : 0) + (hasNote ? 1 : 0);
    const Line = ({ r, child }) => <tr><td style={child ? { paddingLeft: "18px", color: "#555" } : null}>{r.display_name || r.name}</td>{hasNote && <td className="n"><NoteRef n={r.note_ref} /></td>}<td className="n">{money(r.final)}</td>{showBudget && <td className="n">{money(r.budget)}</td>}{showPrior && <td className="n">{money(r.prior)}</td>}</tr>;
    const Row = ({ r }) => r.is_group
        ? <><tr><td>{r.display_name || r.name}</td>{hasNote && <td className="n"><NoteRef n={r.note_ref} /></td>}<td className="n">{money(r.final)}</td>{showBudget && <td className="n">{money(r.budget)}</td>}{showPrior && <td className="n">{money(r.prior)}</td>}</tr>{!r.collapsed && r.children.map((c) => <Line key={c.id} r={c} child />)}</>
        : <Line r={r} />;
    const T = ({ l, c, b, p, s }) => <tr className={s ? "tot strong" : "tot"}><td>{l}</td>{hasNote && <td className="n"></td>}<td className="n">{money(c)}</td>{showBudget && <td className="n">{money(b)}</td>}{showPrior && <td className="n">{money(p)}</td>}</tr>;
    return (
        <div className="stmt">
            {headerHtml ? <div className="rte-content stmt-head" dangerouslySetInnerHTML={{ __html: cleanReportHtml(headerHtml) }} /> : (
                <div className="stmt-head">
                    <h2>{title}</h2>
                    {entity ? <p className="stmt-sub"><strong>{entity}</strong></p> : null}
                    {period ? <p className="stmt-sub"><em>For the {period}</em></p> : null}
                </div>
            )}
            <table><thead><tr><th></th>{hasNote && <th className="n">Note</th>}<th className="n">{L.current} (£)</th>{showBudget && <th className="n">{L.budget} (£)</th>}{showPrior && <th className="n">{L.prior} (£)</th>}</tr></thead>
                <tbody>
                    <tr className="grp"><td colSpan={span}>Income</td></tr>
                    {block.income.map((r) => <Row key={r.id} r={r} />)}
                    <T l="Total income" c={block.total_income} b={block.total_income_budget} p={block.total_income_prior} />
                    <tr className="grp"><td colSpan={span}>Expenditure</td></tr>
                    {block.expenditure.map((r) => <Row key={r.id} r={r} />)}
                    <T l="Total expenditure" c={block.total_expenditure} b={block.total_expenditure_budget} p={block.total_expenditure_prior} />
                    {block.has_contribution ? (<>
                        <T l="Surplus / (deficit) before contribution to Reserves" c={block.surplus_before} b={block.surplus_before_budget} p={block.surplus_before_prior} s />
                        <tr className="grp"><td colSpan={span}>Contribution to / (from) Reserves</td></tr>
                        {block.contributions.map((r) => <Line key={r.id} r={r} />)}
                        <T l="Total contribution to / (from) Reserves" c={block.total_contribution} b={block.total_contribution_budget} p={block.total_contribution_prior} />
                    </>) : null}
                    <T l={`Surplus / (deficit) for the ${period || "period"}`} c={block.surplus} b={block.surplus_budget} p={block.surplus_prior} s />
                </tbody></table>
        </div>
    );
}

function BSBlock({ bs, showPrior, money, agent, entity, headerHtml, labels, hasNote }) {
    const L = labels || { current: "Current Period", prior: "Prior Period", budget: "Budget" };
    hasNote = !!hasNote;
    const Line = ({ r, child }) => <tr><td style={child ? { paddingLeft: "18px", color: "#555" } : null}>{r.display_name || r.name}</td>{hasNote && <td className="n"><NoteRef n={r.note_ref} /></td>}<td className="n">{money(r.final)}</td>{showPrior && <td className="n">{money(r.prior)}</td>}</tr>;
    const Row = ({ r }) => r.is_group
        ? <><tr><td>{r.display_name || r.name}</td>{hasNote && <td className="n"><NoteRef n={r.note_ref} /></td>}<td className="n">{money(r.final)}</td>{showPrior && <td className="n">{money(r.prior)}</td>}</tr>{!r.collapsed && r.children.map((c) => <Line key={c.id} r={c} child />)}</>
        : <Line r={r} />;
    const G = ({ l }) => <tr className="grp"><td colSpan={2 + (showPrior ? 1 : 0) + (hasNote ? 1 : 0)}>{l}</td></tr>;
    const T = ({ l, c, p, s }) => <tr className={s ? "tot strong" : "tot"}><td>{l}</td>{hasNote && <td className="n"></td>}<td className="n">{money(c)}</td>{showPrior && <td className="n">{money(p)}</td>}</tr>;
    return (
        <div className="stmt">
            {headerHtml ? <div className="rte-content stmt-head" dangerouslySetInnerHTML={{ __html: cleanReportHtml(headerHtml) }} /> : <h2>Service Charge Balance Sheet</h2>}
            <table><thead><tr><th></th>{hasNote && <th className="n">Note</th>}<th className="n">{L.current} (£)</th>{showPrior && <th className="n">{L.prior} (£)</th>}</tr></thead>
                <tbody>
                    <G l="Assets" />{bs.assets.map((r) => <Row key={r.id} r={r} />)}<T l="Total assets" c={bs.total_assets} p={bs.total_assets_prior} />
                    <G l="Liabilities" />{bs.liabilities.map((r) => <Row key={r.id} r={r} />)}<T l="Total liabilities" c={bs.total_liabilities} p={bs.total_liabilities_prior} />
                    <T l="Net assets" c={bs.net_assets} p={bs.net_assets_prior} s />
                    <G l="Reserves" />{bs.reserves.map((r) => <Row key={r.id} r={r} />)}<T l="Total reserves" c={bs.total_reserves} p={bs.total_reserves_prior} />
                </tbody></table>
            {agent && !/^self[\s-]*managed$/i.test((agent || "").trim()) ? <p className="approve">This statement of account was approved by the managing agent, {agent}, for and on behalf of {entity}.</p> : null}
        </div>
    );
}

function NotesBlock({ notes, showPrior, money, entity, period, labels, periodEnds }) {
    const L = labels || { current: "Current Period", prior: "Prior Period", budget: "Budget" };
    const list = notes.list || [];
    const RowTable = ({ rows, showTotal, totalLabel, hdr, sp }) => (
            <table><thead><tr><th>{hdr.desc}</th><th className="n">{hdr.current} (£)</th>{sp && <th className="n">{hdr.prior} (£)</th>}</tr></thead>
                <tbody>{rows.map((r, i) => <tr key={i}><td>{r.label}</td><td className="n">{money(r.current)}</td>{sp && <td className="n">{money(r.prior)}</td>}</tr>)}
                    {showTotal && <tr className="tot strong"><td>{totalLabel || "Total"}</td><td className="n">{money(rows.reduce((s, r) => s + r.current, 0))}</td>{sp && <td className="n">{money(rows.reduce((s, r) => s + r.prior, 0))}</td>}</tr>}</tbody></table>
    );
    return (
        <div className="stmt">
            <h2>Notes to the Service Charge Accounts</h2>
            {entity ? <p className="stmt-sub"><strong>{entity}</strong></p> : null}
            {period ? <p className="stmt-sub"><em>For the {period}</em></p> : null}
            {list.map((n) => (
                <div className="note" id={`note-${n.number}`} key={n.key}>
                    <h3>{n.number}. {n.title}</h3>
                    <div className="rte-content" dangerouslySetInnerHTML={{ __html: cleanReportHtml(n.rendered_body || n.body_html) }} />
                    {(n.tables || []).map((tb, ti) => (tb.rows || []).length > 0 && (
                        <div key={ti}>
                            {tb.caption ? <p className="note-tbl-caption">{tb.caption}</p> : null}
                            <RowTable rows={tb.rows} showTotal={tb.show_total !== false} totalLabel={tb.total_label} hdr={noteTableHeaders(tb, L, periodEnds)} sp={showPrior && tb.show_prior !== false} />
                        </div>
                    ))}
                    {n.source === "fund_movement" && (() => {
                        const fm = n.fund_movement || [];
                        const rep = Math.round(fm.filter((r) => r.closing > 0).reduce((s, r) => s + r.closing, 0) * 100) / 100;
                        const rec = Math.round(-fm.filter((r) => r.closing < 0).reduce((s, r) => s + r.closing, 0) * 100) / 100;
                        const net = Math.round((rep - rec) * 100) / 100;
                        const bsF = typeof n.bs_fund === "number" ? n.bs_fund : net;
                        const diff = Math.round((net - bsF) * 100) / 100;
                        return (<>
                            <table><thead><tr><th>Schedule</th><th className="n">Opening (£)</th><th className="n">Balancing charges processed (£)</th><th className="n">Surplus / (deficit) (£)</th><th className="n">Closing (£)</th></tr></thead>
                                <tbody>
                                    {fm.map((r, i) => <tr key={i}><td>{r.schedule}</td><td className="n">{money(r.opening)}</td><td className="n">{money(r.balancing || 0)}</td><td className="n">{money(r.movement)}</td><td className="n">{money(r.closing)}</td></tr>)}
                                    {rep !== 0 && <tr><td colSpan={4}>Surplus repayable to leaseholders</td><td className="n">{money(rep)}</td></tr>}
                                    {rec !== 0 && <tr><td colSpan={4}>Deficit recoverable from leaseholders</td><td className="n">{money(rec)}</td></tr>}
                                </tbody></table>
                            {Math.abs(diff) >= 1 && <p className="note-tbl-caption" style={{ color: "#b45309" }}>These schedule balances net to {money(net)}, differing from the balance-sheet fund position of {money(bsF)} by {money(diff)} (entity-level items not attributed to a schedule).</p>}
                        </>);
                    })()}
                </div>
            ))}
        </div>
    );
}

function PrintShell({ watermark, preview, children }) {
    const [zoom, setZoom] = useState(1);
    const fitWidth = () => {
        const px = 210 * (96 / 25.4); // A4 width in CSS px at 100%
        const z = Math.max(0.3, Math.min(1.6, (window.innerWidth - 96) / px));
        setZoom(Math.round(z * 100) / 100);
    };
    const fitPage = () => {
        const wpx = 210 * (96 / 25.4), hpx = 297 * (96 / 25.4); // whole A4 sheet visible
        const z = Math.min((window.innerWidth - 96) / wpx, (window.innerHeight - 88) / hpx);
        setZoom(Math.max(0.2, Math.min(1.6, Math.round(z * 100) / 100)));
    };
    useEffect(() => { if (preview) fitPage(); /* eslint-disable-next-line */ }, [preview]);
    return (
        <div className={`print-root${preview ? " preview-sheets" : ""}`}>
            <style>{PRINT_CSS}</style>
            {preview && (
                <div className="preview-zoombar no-print" data-testid="preview-zoombar">
                    <button data-testid="preview-zoom-out" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(0.3, Math.round((z - 0.1) * 100) / 100))}><Minus size={15} /></button>
                    <span className="zpct" data-testid="preview-zoom-pct">{Math.round(zoom * 100)}%</span>
                    <button data-testid="preview-zoom-in" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(2, Math.round((z + 0.1) * 100) / 100))}><Plus size={15} /></button>
                    <button className="fit" data-testid="preview-zoom-fit" onClick={fitWidth}>Fit width</button>
                    <button className="fit" data-testid="preview-zoom-fitpage" onClick={fitPage}>Fit page</button>
                </div>
            )}
            <button className="print-btn no-print" data-testid="print-now" onClick={() => window.print()}><Printer size={15} /> Print / Save as PDF</button>
            {watermark && <div className="watermark" aria-hidden>DRAFT</div>}
            {preview ? <div className="print-zoom" style={{ zoom }}>{children}</div> : children}
        </div>
    );
}

const PRINT_CSS = `
.print-root{background:#fff;color:#111;font-family:'IBM Plex Sans',ui-sans-serif,system-ui,sans-serif;}
.print-btn{position:fixed;top:16px;right:16px;z-index:1000;background:#1e5a85;color:#fff;border:none;border-radius:999px;padding:10px 16px;font-family:'IBM Plex Sans',sans-serif;font-size:13px;display:flex;gap:8px;align-items:center;cursor:pointer;box-shadow:0 8px 20px -6px rgba(30,90,133,.5)}
.preview-zoombar{position:fixed;top:16px;left:16px;z-index:1000;display:flex;align-items:center;gap:6px;background:#2b2e33;border:1px solid #3a3e44;border-radius:999px;padding:5px 8px;box-shadow:0 8px 20px -6px rgba(0,0,0,.6)}
.preview-zoombar button{background:#1e5a85;color:#fff;border:none;border-radius:999px;width:28px;height:28px;display:flex;align-items:center;justify-content:center;cursor:pointer}
.preview-zoombar button.fit{width:auto;padding:0 12px;font-family:'IBM Plex Sans',sans-serif;font-size:12px;height:28px}
.preview-zoombar .zpct{color:#e7eaee;font-family:'IBM Plex Mono',monospace;font-size:12px;min-width:44px;text-align:center}
.break-hint{position:absolute;left:0;right:0;pointer-events:none;z-index:40;border-top:1px dashed rgba(30,90,133,.7)}
.break-hint-label{position:absolute;right:6px;top:-9px;background:#1e5a85;color:#fff;font-family:'IBM Plex Sans',sans-serif;font-size:9px;letter-spacing:.03em;padding:2px 8px;border-radius:999px;white-space:nowrap}
.break-hint-btn{position:absolute;right:6px;top:-11px;background:#1e5a85;color:#fff;border:none;font-family:'IBM Plex Sans',sans-serif;font-size:9px;letter-spacing:.03em;padding:3px 10px;border-radius:999px;white-space:nowrap;cursor:pointer;pointer-events:auto;box-shadow:0 2px 6px rgba(0,0,0,.3)}
.break-hint-btn:hover{background:#17496b}
.break-existing{position:absolute;left:0;right:0;pointer-events:none;z-index:41;border-top:1px solid rgba(180,71,47,.65)}
.break-existing-tools{position:absolute;left:6px;top:-12px;display:flex;align-items:center;gap:4px;pointer-events:auto}
.break-move{background:#5a4231;color:#fff;border:none;width:22px;height:22px;display:flex;align-items:center;justify-content:center;border-radius:999px;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.3)}
.break-move:hover{background:#6f5340}
.break-remove{display:flex;align-items:center;gap:3px;background:#b4472f;color:#fff;border:none;font-family:'IBM Plex Sans',sans-serif;font-size:9px;letter-spacing:.03em;padding:0 10px;height:22px;border-radius:999px;white-space:nowrap;cursor:pointer;box-shadow:0 2px 6px rgba(0,0,0,.3)}
.break-remove:hover{background:#97371f}
.watermark{position:fixed;inset:0;z-index:5;display:flex;align-items:center;justify-content:center;pointer-events:none;font-family:'Chivo',sans-serif;font-weight:800;font-size:180px;color:rgba(200,0,0,.10);transform:rotate(-35deg);letter-spacing:20px}
.page{max-width:820px;margin:0 auto;padding:48px 56px;position:relative;z-index:10}
.page + .page{border-top:1px dashed #ddd}
.cover{min-height:60vh;display:flex;align-items:center}
.cover-inner{text-align:center;width:100%}
.cover .brand{font-family:'Chivo',sans-serif;letter-spacing:5px;color:#1e5a85;font-weight:700;font-size:13px;margin-bottom:40px}
.cover h1{font-family:'Chivo',sans-serif;font-weight:800;font-size:34px;margin:0 0 6px}
.cover .prop{color:#555;font-size:16px;margin-bottom:24px}
.cover .doctitle{font-family:'Chivo',sans-serif;font-size:24px;color:#1e5a85;margin:18px 0 6px}
.cover .period{font-size:16px;color:#333}
.cover .agent{margin-top:24px;color:#555;font-size:14px}
.cover .prep{margin-top:60px;color:#777;font-family:'IBM Plex Sans',sans-serif;font-size:13px}
.page h2{font-size:20px;color:#1e5a85;margin:0 0 14px;font-family:'Chivo',sans-serif;font-weight:700}
.stmt .stmt-sub{margin:0 0 4px;font-size:14px;color:#333}
.note .note-tbl-caption{font-weight:600;margin:10px 0 2px;font-size:14px}
.stmt .stmt-sub + table{margin-top:12px}
.contents{font-size:15px;line-height:2.2;list-style:none;padding:0;counter-reset:none}
.contents li{display:flex;align-items:baseline;gap:8px}
.contents a{color:#111;text-decoration:none;white-space:nowrap}
.contents a:hover{color:#1e5a85;text-decoration:underline}
.contents .dots{flex:1 1 auto;border-bottom:1px dotted #bbb;transform:translateY(-3px)}
.contents .pg{font-family:'IBM Plex Mono',monospace;color:#1e5a85;font-weight:600}
.pagenum{margin-top:18px;text-align:center;font-family:'IBM Plex Mono',monospace;font-size:11px;color:#889;letter-spacing:.06em}
.stmt td.n a.noteref{color:#1e5a85;text-decoration:none;font-weight:600}
.share-foot{margin-top:26px;font-size:12px;color:#556;border-top:1px dotted #ccc;padding-top:10px}
.share-foot a{color:#1e5a85;font-weight:600}
.note-hovercard{background:#fff;border:1px solid #d5dbe1;border-radius:6px;box-shadow:0 12px 30px -8px rgba(20,40,60,.35);padding:12px 14px;font-family:'IBM Plex Sans',sans-serif;font-size:12px;line-height:1.55;color:#222}
.note-hovercard .hc-title{font-family:'Chivo',sans-serif;font-weight:700;color:#1e5a85;font-size:13px;margin-bottom:6px}
.note-hovercard table{border-collapse:collapse;width:100%;margin:6px 0}
.note-hovercard th,.note-hovercard td{border-bottom:1px solid #eee;padding:3px 6px;font-size:12px}
.note-hovercard th.n,.note-hovercard td.n{text-align:right;font-variant-numeric:tabular-nums}
.note-hovercard tr.tot td{border-top:1px solid #999;font-weight:600}
.note{scroll-margin-top:14mm}
.rte-content{font-size:14px;line-height:1.65}
.rte-content h2{font-family:'Chivo',sans-serif;color:#1e5a85;font-size:20px}
.rte-content h3{font-family:'Chivo',sans-serif;color:#1e5a85;text-transform:uppercase;font-size:14px;letter-spacing:.04em;margin-top:16px}
.rte-content table{border-collapse:collapse;width:100%;margin:10px 0}
.rte-content th,.rte-content td{border:1px solid #ccc;padding:5px 8px;font-size:13px}
.stmt table{border-collapse:collapse;width:100%;margin-top:8px}
.stmt th,.stmt td{padding:5px 8px;font-size:13px;border-bottom:1px solid #eee}
.stmt th.n,.stmt td.n{text-align:right;font-variant-numeric:tabular-nums;font-family:'IBM Plex Mono',ui-monospace,monospace;font-feature-settings:"tnum" 1,"zero" 1}
.stmt tr.grp td{background:#f4f7fa;font-family:'Chivo',sans-serif;text-transform:uppercase;font-size:10px;letter-spacing:.1em;color:#556}
.stmt tr.tot td{border-top:1px solid #999;font-weight:600}
.stmt tr.tot.strong td{border-top:2px solid #333}
.stmt .approve{margin-top:16px;font-size:13px;color:#444}
.note{margin-bottom:22px}
.note h3{font-family:'Chivo',sans-serif;font-size:15px;color:#1e5a85}
.manual-break{display:block;height:0;border:0;margin:0}
/* On-screen "PDF preview" — render true A4 sheets so staff see exactly what will export. */
@media screen{
  .preview-sheets{background:#51545a;padding:28px 0;min-height:100vh}
  .preview-sheets .page{width:210mm;max-width:210mm;min-height:297mm;box-sizing:border-box;padding:18mm 16mm;margin:0 auto 20px;background:#fff;box-shadow:0 4px 18px rgba(0,0,0,.45);display:flex;flex-direction:column}
  .preview-sheets .page + .page{border-top:none}
  .preview-sheets .cover{align-items:center;justify-content:center}
  .preview-sheets .pagenum{margin-top:auto;padding-top:10mm}
  /* faint guide line at each A4 page edge so multi-page sections show where they'll break */
  .preview-sheets .brk{background-image:repeating-linear-gradient(to bottom,transparent 0,transparent 296mm,rgba(30,90,133,.16) 296mm,rgba(30,90,133,.16) 297mm);background-origin:border-box}
}
@media print{
  .no-print{display:none !important}
  .print-zoom{zoom:1 !important}
  .page{padding:6mm 4mm;max-width:100%}
  .page + .page{border-top:none}
  .brk{break-after:page;page-break-after:always}
  .brk:not(.cover){box-sizing:border-box;display:flex;flex-direction:column;min-height:calc(297mm - 24mm);break-inside:auto}
  .brk:not(.cover) > .pagenum{margin-top:auto}
  .pagenum{display:none}
  .cover{min-height:auto;padding-top:22mm}
  .note{break-inside:avoid;page-break-inside:avoid}
  .stmt tr{break-inside:avoid;page-break-inside:avoid}
  .rte-content table,.rte-content tr{break-inside:avoid;page-break-inside:avoid}
  .manual-break{break-before:page;page-break-before:always}
  .watermark{position:fixed}
  .note-hovercard{display:none !important}
  @page{size:A4;margin:12mm;@bottom-center{content:"Page " counter(page);font-family:'IBM Plex Mono',ui-monospace,monospace;font-size:9pt;color:#66707d}}
  @page:first{@bottom-center{content:""}}
}`;
